// Payroll engine — the ONE place staff pay is computed.
//
// Before this module the web app had two engines (staffPeriodPay for the
// summary / 12-month chart / "unpaid" reminder, and an inline block for the
// payslip) that disagreed: only one prorated base salary, only the other paid
// part-timers commission or counted per-service rates, tips, bonuses and
// deductions. The native app copied both. Every screen now calls computePay()
// so the number an owner sees on the summary is the number on the payslip.
//
// Pure: no React, no Supabase. Pass in the data; app-specific money helpers
// (booking revenue, per-visit package value) are injected so the maths can be
// unit-tested in isolation (payroll.test.mjs, run with `npm test`).
//
// Money is HKD. Each line item is rounded to whole dollars ONCE, and gross /
// net are sums of the rounded lines, so a payslip always adds up exactly.

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const r = (v) => Math.round(num(v));

// ── Dates (YYYY-MM-DD strings, local calendar days — no timezone maths) ──
const parse = (s) => { const [y, m, d] = String(s).split("-").map(Number); return { y, m, d }; };
const toKey = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const dayIndex = (s) => { const { y, m, d } = parse(s); return Date.UTC(y, m - 1, d) / 86400000; };
const maxKey = (a, b) => (a > b ? a : b);
const minKey = (a, b) => (a < b ? a : b);

/** Inclusive number of days in [from, to]; 0 if to < from. */
export function daysBetween(from, to) {
  if (!from || !to || to < from) return 0;
  return dayIndex(to) - dayIndex(from) + 1;
}

/**
 * Monthly base salary earned in [from, to].
 *
 * Base is a MONTHLY figure. For every calendar month the period touches, pay
 * (employed days in that month ∩ period) / (days in that month) × base. So a
 * full calendar month is exactly the base, a single day is ~1/30 of it, and a
 * mid-month join or leave is prorated. The old code paid a full month for any
 * period — pick 今日 on the payslip and it showed the whole month's salary.
 */
export function proratedBase(base, from, to, joinDate, leaveDate) {
  const b = num(base);
  if (b <= 0 || !from || !to || to < from) return { amount: 0, employedDays: 0, periodDays: daysBetween(from, to) };
  const start = joinDate ? maxKey(from, joinDate) : from;
  const end = leaveDate ? minKey(to, leaveDate) : to;
  let amount = 0, employedDays = 0;
  if (end >= start) {
    let { y, m } = parse(start);
    const endP = parse(end);
    while (y < endP.y || (y === endP.y && m <= endP.m)) {
      const dim = daysInMonth(y, m);
      const mStart = maxKey(start, toKey(y, m, 1));
      const mEnd = minKey(end, toKey(y, m, dim));
      const days = daysBetween(mStart, mEnd);
      employedDays += days;
      amount += b * days / dim;
      m += 1; if (m > 12) { m = 1; y += 1; }
    }
  }
  return { amount: r(amount), employedDays, periodDays: daysBetween(from, to) };
}

/** Marginal tiered commission over ONE revenue total (bands by fromRev). */
export function tieredCommission(tiers, revenue) {
  const rev = num(revenue);
  const bands = (Array.isArray(tiers) ? tiers : []).filter((t) => t && num(t.rate) > 0);
  if (!bands.length || rev <= 0) return 0;
  const sorted = [...bands].sort((a, b) => num(a.fromRev) - num(b.fromRev));
  let comm = 0;
  for (let i = 0; i < sorted.length; i++) {
    const start = num(sorted[i].fromRev);
    const end = sorted[i + 1] ? num(sorted[i + 1].fromRev) : Infinity;
    if (rev <= start) break;
    comm += (Math.min(rev, end) - start) * num(sorted[i].rate) / 100;
    if (rev <= end) break;
  }
  return comm;
}

export const DEFAULT_SETTINGS = Object.freeze({
  baseSalary: 0,
  hourlyRate: 0,
  commissionEnabled: true,
  commissionMode: "percent",      // "percent" of revenue | "fixed" $ per order
  commissionRate: 0,
  commissionTiers: [],
  serviceCommissionRates: {},     // { [serviceId]: percent } — wins over the rest
  packageCommissionRate: 0,       // % of package SALES the staff sold
  packageRedemptionCommissionRate: 0, // % of per-visit value on package visits
});

/**
 * Compute one staff member's pay for [from, to].
 *
 * @param {object} p
 * @param {{id:string, jobType?:string, joinDate?:string, leaveDate?:string}} p.staff
 * @param {object} [p.settings]    pay settings (DEFAULT_SETTINGS shape)
 * @param {object[]} [p.apts]      appointments ({staffId,status,date,svcId,customerPackageId,tipAmount})
 * @param {object[]} [p.wages]     wage entries ({date|workDate, hours, amount})
 * @param {object[]} [p.adjustments] staff_deductions rows ({date, kind, amount})
 * @param {object[]} [p.packagesSold] customer packages ({soldByUserId, purchasedDate, amountPaid, refundedAt})
 * @param {string} p.from          YYYY-MM-DD inclusive
 * @param {string} p.to            YYYY-MM-DD inclusive
 * @param {(apt:object)=>number} p.revenueOf        cash revenue of a booking (package base excluded)
 * @param {(apt:object)=>number} [p.packageVisitValueOf] per-visit value of a package booking
 */
export function computePay({
  staff, settings, apts = [], wages = [], adjustments = [], packagesSold = [],
  from, to, revenueOf, packageVisitValueOf = () => 0,
}) {
  const s = { ...DEFAULT_SETTINGS, ...(settings || {}) };
  const isFull = staff?.jobType === "fulltime";
  const inRange = (d) => !!d && d >= from && d <= to;

  // Base (full-timers) — prorated by calendar month and employment dates.
  const base = isFull
    ? proratedBase(s.baseSalary, from, to, staff?.joinDate, staff?.leaveDate)
    : { amount: 0, employedDays: 0, periodDays: daysBetween(from, to) };

  // Hourly / daily wage (part-timers) — the wage entries ARE the pay record
  // (an amount may be a day-rate override, not hours × rate).
  const wageRows = (wages || []).filter((w) => w && inRange(w.date || w.workDate));
  const hourly = isFull
    ? { amount: 0, hours: 0, days: 0 }
    : {
        amount: wageRows.reduce((a, w) => a + r(w.amount), 0),
        hours: wageRows.reduce((a, w) => a + num(w.hours), 0),
        days: new Set(wageRows.map((w) => w.date || w.workDate)).size,
      };

  // Completed bookings in the period (commission basis: status "done").
  const done = (apts || []).filter((a) => a && a.staffId === staff?.id && a.status === "done" && inRange(a.date));
  const pkgApts = done.filter((a) => a.customerPackageId);
  const regApts = done.filter((a) => !a.customerPackageId);
  const revOf = (a) => num(revenueOf ? revenueOf(a) : 0);
  const orderRevenue = regApts.reduce((a, x) => a + revOf(x), 0);

  let serviceCommission = 0;
  let serviceCommissionLabel = "";
  let redemptionCommission = 0;
  if (s.commissionEnabled !== false) {
    const overrides = (s.serviceCommissionRates && typeof s.serviceCommissionRates === "object") ? s.serviceCommissionRates : {};
    const hasOverride = (a) => a.svcId && Object.prototype.hasOwnProperty.call(overrides, a.svcId);
    // 1) Per-service overrides win for their own bookings.
    const overrideComm = regApts.filter(hasOverride).reduce((acc, a) => acc + revOf(a) * num(overrides[a.svcId]) / 100, 0);
    // 2) Everything else is POOLED and goes through tiers / flat rate once.
    //    The old code ran tiers per booking when overrides existed, so every
    //    booking restarted at the bottom band and higher bands never applied.
    const pool = regApts.filter((a) => !hasOverride(a));
    const poolRev = pool.reduce((acc, a) => acc + revOf(a), 0);
    const tiers = (Array.isArray(s.commissionTiers) ? s.commissionTiers : []).filter((t) => t && num(t.rate) > 0);
    let poolComm = 0;
    if (tiers.length) { poolComm = tieredCommission(tiers, poolRev); serviceCommissionLabel = "階梯式"; }
    else if (s.commissionMode === "percent") { poolComm = poolRev * num(s.commissionRate) / 100; serviceCommissionLabel = num(s.commissionRate) + "%"; }
    else { poolComm = pool.length * num(s.commissionRate); serviceCommissionLabel = "每單 $" + num(s.commissionRate); }
    if (Object.keys(overrides).length && regApts.some(hasOverride)) serviceCommissionLabel = "個別服務" + (pool.length ? " + " + serviceCommissionLabel : "");
    serviceCommission = r(overrideComm + poolComm);

    // 3) Package visits: per-visit value + any cash add-ons on that visit.
    const pr = num(s.packageRedemptionCommissionRate);
    if (pr > 0) {
      redemptionCommission = r(pkgApts.reduce((acc, a) => acc + (num(packageVisitValueOf(a)) + revOf(a)) * pr / 100, 0));
    }
  }

  // Package SALES commission (who sold the package), independent of the
  // service-commission switch — same rule the payslip always used.
  const sold = (packagesSold || []).filter((c) => c && c.soldByUserId === staff?.id && !c.refundedAt && inRange(c.purchasedDate));
  const soldRevenue = sold.reduce((a, c) => a + num(c.amountPaid), 0);
  const packageSalesCommission = r(soldRevenue * num(s.packageCommissionRate) / 100);

  const tips = done.reduce((a, x) => a + r(x.tipAmount), 0);

  const adj = (adjustments || []).filter((d) => d && inRange(d.date));
  const bonus = adj.filter((d) => d.kind === "bonus").reduce((a, d) => a + r(d.amount), 0);
  const deductions = adj.filter((d) => d.kind !== "bonus").reduce((a, d) => a + r(d.amount), 0);

  const gross = base.amount + hourly.amount + serviceCommission + redemptionCommission + packageSalesCommission + tips + bonus;
  const net = gross - deductions;

  return {
    from, to,
    jobType: isFull ? "fulltime" : "parttime",
    base,
    hourly,
    orders: { count: regApts.length, packageVisits: pkgApts.length, revenue: r(orderRevenue) },
    serviceCommission, serviceCommissionLabel,
    redemptionCommission, redemptionRate: num(s.packageRedemptionCommissionRate),
    packageSales: { count: sold.length, revenue: r(soldRevenue), rate: num(s.packageCommissionRate), commission: packageSalesCommission },
    tips,
    bonus,
    deductions,
    adjustments: adj,
    gross,
    net,
    // MPF is NOT computed (statutory work pending). Every surface that shows
    // `net` must say so — see payroll audit 2026-09-28.
    mpfIncluded: false,
  };
}

// ── Payout helpers (period overlap = double-pay risk) ─────────────────────
export const payoutsOverlapping = (payouts, from, to) =>
  (payouts || []).filter((p) => p && !p.voidedAt && p.kind !== "adjustment" && p.from <= to && from <= p.to);
export const payoutCovering = (payouts, from, to) =>
  (payouts || []).find((p) => p && !p.voidedAt && p.kind !== "adjustment" && p.from <= from && p.to >= to);
